import { createRoot } from "react-dom/client";
import { DJSEMWorkspaceApp, DJSEMErrorBoundary, DJSEMApiClient, createWebPlatform, resolveWebApiBase } from "@djsem/ui";
import { LicensePurchasePage, LicenseSuccessPage, LicenseCancelledPage } from "./LicensePages";
import { LiveAudienceFeedbackPage, LivePromoterFeedbackPage } from "./LiveFeedbackPage";

// DJSEM Web entry.
//
// This is a THIN mount: all app UI lives in the shared @djsem/ui DJSEMWorkspaceApp, which is the
// exact same renderer the Electron desktop uses. The only host-specific step is building the
// platform adapter — here the web one, which has no live bridge and resolves the API base from
// Vite env (VITE_API_BASE) or same-origin in production.
//
// Result: the workspace runs 1:1 in the browser. Live-only panels degrade gracefully (the bridge
// reports "disconnected"); everything else — shell, navigation, all data modules, API wiring —
// behaves identically to the desktop.
//
// The one exception: /license, /license/success, /license/cancelled (perpetual desktop license
// purchase, task #45 Phase E — see LicensePages.tsx) are plain pages reached before/without any
// account session, so they're checked here rather than added to a router this app doesn't have.

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? undefined;
const sameOrigin = import.meta.env.PROD;
const platform = createWebPlatform({
  apiBase,
  // In production with no explicit VITE_API_BASE, use same-origin (worker deployed behind the
  // same host). In dev, resolveWebApiBase falls back to the local worker (http://localhost:8787).
  sameOrigin,
});

const path = window.location.pathname.replace(/\/+$/, "") || "/";
const licensePage =
  path === "/license" ? "purchase" :
  path === "/license/success" ? "success" :
  path === "/license/cancelled" ? "cancelled" :
  null;

// Prompt 150: anonymous live feedback (/feedback/:token) and promoter feedback
// (/feedback/:token/promoter) — reached via a DJ-generated QR code / link, no account. Same
// "plain page, no router" handling as the license pages above.
const feedbackMatch = /^\/feedback\/([^/]+)(\/promoter)?$/.exec(path);

if (licensePage) {
  const client = new DJSEMApiClient({ baseUrl: resolveWebApiBase({ apiBase, sameOrigin }) });
  const page = licensePage === "purchase" ? <LicensePurchasePage client={client} />
    : licensePage === "success" ? <LicenseSuccessPage client={client} />
    : <LicenseCancelledPage />;
  createRoot(document.getElementById("root")!).render(
    <DJSEMErrorBoundary onError={(error) => console.error("[DJSEM] render error", error)}>{page}</DJSEMErrorBoundary>,
  );
} else if (feedbackMatch) {
  const client = new DJSEMApiClient({ baseUrl: resolveWebApiBase({ apiBase, sameOrigin }) });
  const [, token, isPromoter] = feedbackMatch;
  const page = isPromoter ? <LivePromoterFeedbackPage client={client} token={token} /> : <LiveAudienceFeedbackPage client={client} token={token} />;
  createRoot(document.getElementById("root")!).render(
    <DJSEMErrorBoundary onError={(error) => console.error("[DJSEM] render error", error)}>{page}</DJSEMErrorBoundary>,
  );
} else {
  createRoot(document.getElementById("root")!).render(
    <DJSEMErrorBoundary onError={(error) => console.error("[DJSEM] render error", error)}>
      <DJSEMWorkspaceApp platform={platform} />
    </DJSEMErrorBoundary>,
  );
}
